/**
 * Permission Evaluation Engine
 * Handles complex permission checks with caching and conditions
 */

import { PERMISSION_GROUPS, SCOPES, FIELD_LEVELS } from '../config/permissions-v2';

class PermissionEngine {
  constructor(userProfile) {
    this.userProfile = userProfile;
    this.cache = new Map();
    this.compiledPermissions = null;
    this.compilePermissions();
  }

  /**
   * Compile user permissions from various sources
   */
  compilePermissions() {
    const compiled = {
      explicit: {},
      denials: new Set(),
      resources: {},
      conditions: {},
    };

    // 1. Process role-based permissions (if inheritRole is true)
    if (this.userProfile?.permissions?.inheritRole && this.userProfile?.role) {
      const rolePermissions = this.getRolePermissions(this.userProfile.role);
      Object.assign(compiled.explicit, rolePermissions);
    }

    // 2. Process permission groups
    if (this.userProfile?.permissions?.groups) {
      this.userProfile.permissions.groups.forEach(groupId => {
        const groupPermissions = this.getGroupPermissions(groupId);
        Object.assign(compiled.explicit, groupPermissions);
      });
    }

    // 3. Process explicit module permissions
    if (this.userProfile?.permissions?.modules) {
      Object.entries(this.userProfile.permissions.modules).forEach(([permission, config]) => {
        compiled.explicit[permission] = config;
        if (config.conditions) {
          compiled.conditions[permission] = config.conditions;
        }
      });
    }

    // 4. Process denials (highest priority)
    if (this.userProfile?.permissions?.denials) {
      this.userProfile.permissions.denials.forEach(denial => {
        compiled.denials.add(denial);
      });
    }

    // 5. Process resource-specific permissions
    if (this.userProfile?.permissions?.resources) {
      compiled.resources = this.userProfile.permissions.resources;
    }

    this.compiledPermissions = compiled;
  }

  /**
   * Main permission check method
   * @param {string} permission - Permission string (e.g., 'dashboard.analytics.view')
   * @param {Object} options - Additional options for the check
   * @returns {Object} Result with allowed status and details
   */
  can(permission, options = {}) {
    const {
      resource = null,
      scope = null,
      fields = null,
      context = {},
    } = options;

    // Check cache
    const cacheKey = this.getCacheKey(permission, options);
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Perform evaluation
    const result = this.evaluate(permission, { resource, scope, fields, context });
    
    // Cache result
    this.cache.set(cacheKey, result);
    return result;
  }

  /**
   * Evaluate permission against all rules
   */
  evaluate(permission, options) {
    const { resource, scope, fields, context } = options;

    // 1. Check explicit denials first (wildcards supported)
    if (this.isDenied(permission)) {
      return {
        allowed: false,
        reason: 'Explicitly denied',
        source: 'denial',
      };
    }

    // 2. Check resource-specific permissions
    if (resource) {
      const resourceCheck = this.checkResourcePermission(resource, permission);
      if (resourceCheck !== null) {
        return resourceCheck;
      }
    }

    // 3. Check explicit permissions
    const permissionConfig = this.getPermissionConfig(permission);
    if (!permissionConfig) {
      return {
        allowed: false,
        reason: 'No permission granted',
        source: 'none',
      };
    }

    // 4. Check scope constraints
    if (scope && !this.checkScope(permissionConfig.scope, scope)) {
      return {
        allowed: false,
        reason: 'Insufficient scope',
        source: 'scope',
        required: permissionConfig.scope,
        provided: scope,
      };
    }

    // 5. Check field-level access
    if (fields && permissionConfig.fields) {
      const allowedFields = this.checkFields(permissionConfig.fields, fields);
      if (allowedFields.length === 0) {
        return {
          allowed: false,
          reason: 'No field access',
          source: 'fields',
        };
      }
    }

    // 6. Check conditions
    if (permissionConfig.conditions) {
      const conditionCheck = this.checkConditions(permissionConfig.conditions, context);
      if (!conditionCheck.allowed) {
        return {
          allowed: false,
          reason: conditionCheck.reason,
          source: 'condition',
          condition: conditionCheck.condition,
        };
      }
    }

    // Permission granted
    return {
      allowed: true,
      source: permissionConfig.source || 'explicit',
      scope: permissionConfig.scope,
      fields: permissionConfig.fields,
      conditions: permissionConfig.conditions,
    };
  }

  /**
   * Check if permission is explicitly denied
   */
  isDenied(permission) {
    return Array.from(this.compiledPermissions.denials).some(denial => {
      if (denial.includes('*')) {
        const pattern = denial.replace(/\*/g, '.*');
        return new RegExp(`^${pattern}$`).test(permission);
      }
      return denial === permission;
    });
  }

  /**
   * Check resource-specific permissions
   */
  checkResourcePermission(resource, permission) {
    const [module, resourceType, action] = permission.split('.');
    
    for (const [pattern, config] of Object.entries(this.compiledPermissions.resources)) {
      const resourcePattern = pattern.replace(/\*/g, '.*');
      if (new RegExp(`^${resourcePattern}$`).test(resource)) {
        if (config.actions && config.actions.includes(action)) {
          // Check expiry
          if (config.expires && new Date(config.expires) < new Date()) {
            return {
              allowed: false,
              reason: 'Permission expired',
              source: 'resource',
              expiredAt: config.expires,
            };
          }
          
          return {
            allowed: true,
            source: 'resource',
            fields: config.fields,
          };
        }
      }
    }
    
    return null;
  }

  /**
   * Get permission configuration from compiled permissions
   */
  getPermissionConfig(permission) {
    // Direct match
    if (this.compiledPermissions.explicit[permission]) {
      return {
        ...this.compiledPermissions.explicit[permission],
        source: 'explicit',
      };
    }

    // Wildcard match
    for (const [pattern, config] of Object.entries(this.compiledPermissions.explicit)) {
      if (pattern.includes('*')) {
        const regex = new RegExp(`^${pattern.replace(/\*/g, '.*')}$`);
        if (regex.test(permission)) {
          return {
            ...config,
            source: 'wildcard',
          };
        }
      }
    }

    return null;
  }

  /**
   * Check if requested scope is within allowed scope
   */
  checkScope(allowedScope, requestedScope) {
    const scopeHierarchy = {
      [SCOPES.OWN]: 1,
      [SCOPES.TEAM]: 2,
      [SCOPES.DEPARTMENT]: 3,
      [SCOPES.LOCATION]: 3,
      [SCOPES.ALL]: 4,
    };

    return scopeHierarchy[allowedScope] >= scopeHierarchy[requestedScope];
  }

  /**
   * Check field-level access
   */
  checkFields(allowedFields, requestedFields) {
    if (!Array.isArray(requestedFields)) {
      requestedFields = [requestedFields];
    }
    
    return requestedFields.filter(field => allowedFields.includes(field));
  }

  /**
   * Check permission conditions
   */
  checkConditions(conditions, context) {
    for (const [conditionType, conditionConfig] of Object.entries(conditions)) {
      switch (conditionType) {
        case 'timeRestriction':
          if (!this.checkTimeRestriction(conditionConfig, context)) {
            return { allowed: false, reason: 'Outside allowed time', condition: conditionType };
          }
          break;
          
        case 'locationRestriction':
          if (!this.checkLocationRestriction(conditionConfig, context)) {
            return { allowed: false, reason: 'Outside allowed location', condition: conditionType };
          }
          break;
          
        case 'temporaryAccess':
          if (!this.checkTemporaryAccess(conditionConfig)) {
            return { allowed: false, reason: 'Temporary access expired', condition: conditionType };
          }
          break;
          
        case 'approvalRequired':
          if (!this.checkApproval(conditionConfig, context)) {
            return { allowed: false, reason: 'Approval required', condition: conditionType };
          }
          break;
      }
    }
    
    return { allowed: true };
  }

  /**
   * Check time-based restrictions
   */
  checkTimeRestriction(config, context) {
    const now = new Date();
    const timezone = config.timezone || 'UTC';
    
    // Check time of day
    if (config.startTime && config.endTime) {
      const currentTime = now.toLocaleTimeString('en-US', { 
        timeZone: timezone, 
        hour12: false 
      });
      
      if (currentTime < config.startTime || currentTime > config.endTime) {
        return false;
      }
    }
    
    // Check days of week
    if (config.daysOfWeek && config.daysOfWeek.length > 0) {
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const currentDay = dayNames[now.getDay()];
      
      if (!config.daysOfWeek.includes(currentDay)) {
        return false;
      }
    }
    
    return true;
  }

  /**
   * Check location-based restrictions
   */
  checkLocationRestriction(config, context) {
    // Check allowed locations
    if (config.allowedLocations && context.location) {
      if (!config.allowedLocations.includes(context.location)) {
        return false;
      }
    }
    
    // Check IP restrictions
    if (config.allowedIPs && context.ip) {
      if (!config.allowedIPs.includes(context.ip)) {
        return false;
      }
    }
    
    return true;
  }

  /**
   * Check temporary access validity
   */
  checkTemporaryAccess(config) {
    const now = new Date();
    
    if (config.startDate && new Date(config.startDate) > now) {
      return false;
    }
    
    if (config.endDate && new Date(config.endDate) < now) {
      return false;
    }
    
    return true;
  }

  /**
   * Check if approval requirements are met
   */
  checkApproval(config, context) {
    if (!context.approvals) {
      return false;
    }
    
    const requiredApprovers = config.approvers || [];
    const receivedApprovals = context.approvals || [];
    
    if (config.approvalLevel === 'all') {
      return requiredApprovers.every(approver => 
        receivedApprovals.includes(approver)
      );
    } else {
      return requiredApprovers.some(approver => 
        receivedApprovals.includes(approver)
      );
    }
  }

  /**
   * Get role-based permissions
   */
  getRolePermissions(role) {
    // This would typically fetch from your existing role permissions
    // For now, returning empty object to be integrated with existing system
    return {};
  }

  /**
   * Get group permissions with inheritance
   */
  getGroupPermissions(groupId) {
    const group = PERMISSION_GROUPS[groupId];
    if (!group) return {};
    
    let permissions = {};
    
    // Process inherited groups first
    if (group.inherits) {
      group.inherits.forEach(inheritedGroupId => {
        const inheritedPermissions = this.getGroupPermissions(inheritedGroupId);
        permissions = { ...permissions, ...inheritedPermissions };
      });
    }
    
    // Apply this group's permissions (overriding inherited ones)
    if (group.permissions) {
      permissions = { ...permissions, ...group.permissions };
    }
    
    return permissions;
  }

  /**
   * Generate cache key for permission check
   */
  getCacheKey(permission, options) {
    return `${permission}:${JSON.stringify(options)}`;
  }

  /**
   * Clear permission cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Update user profile and recompile permissions
   */
  updateUserProfile(newProfile) {
    this.userProfile = newProfile;
    this.clearCache();
    this.compilePermissions();
  }

  /**
   * Get all permissions for the user
   */
  getAllPermissions() {
    const permissions = [];
    
    Object.entries(this.compiledPermissions.explicit).forEach(([permission, config]) => {
      if (!this.isDenied(permission)) {
        permissions.push({
          permission,
          ...config,
        });
      }
    });
    
    return permissions;
  }

  /**
   * Check multiple permissions at once
   */
  canAny(permissions, options = {}) {
    return permissions.some(permission => 
      this.can(permission, options).allowed
    );
  }

  /**
   * Check if user has all specified permissions
   */
  canAll(permissions, options = {}) {
    return permissions.every(permission => 
      this.can(permission, options).allowed
    );
  }
}

export default PermissionEngine;